import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Users, UserPlus, Eye } from 'lucide-react';

export default function ClientsPage() {
  const { user, axiosAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const api = axiosAuth();
  const [clients, setClients] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [recentActiveIds, setRecentActiveIds] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ client_id: '', specialist_id: '' });
  const [heatCompletions, setHeatCompletions] = useState([]);
  const [herCompletions, setHerCompletions] = useState([]);

  const programFilter = (searchParams.get('program') || '').toUpperCase();
  const statusFilter = (searchParams.get('status') || '').toLowerCase();
  const activityFilter = (searchParams.get('activity') || '').toLowerCase();
  const assignedFilter = searchParams.get('assigned');
  const isReferral = ['referral_source', 'court_officer'].includes(user.role);
  const canCheckActivity = ['admin', 'coordinator', 'peer_specialist', 'network_admin'].includes(user.role);

  const load = async () => {
    try {
      let list = [];
      if (programFilter === 'HEAT') {
        const r = await api.get('/heat/participants');
        list = r.data;
      } else if (programFilter === 'HER') {
        const r = await api.get('/her/participants');
        list = r.data;
      } else if (isReferral) {
        const [heat, her] = await Promise.all([
          api.get('/heat/participants').catch(() => ({ data: [] })),
          api.get('/her/participants').catch(() => ({ data: [] }))
        ]);
        const map = new Map();
        [...heat.data, ...her.data].forEach(c => { map.set(c.id, c); });
        list = Array.from(map.values());
      } else {
        const r = await api.get('/users?role=client');
        list = r.data;
      }
      setClients(list);

      if (['coordinator', 'admin'].includes(user.role)) {
        const r = await api.get('/users?role=peer_specialist');
        setSpecialists(r.data);
      }
    } catch {
      setClients([]);
    }
  };

  useEffect(() => { load(); }, [programFilter]);

  useEffect(() => {
    if (!activityFilter) {
      setRecentActiveIds([]);
      setActivityLoading(false);
      return;
    }
    if (!canCheckActivity) {
      setRecentActiveIds([]);
      return;
    }
    const parsed = parseInt(activityFilter.replace('d', ''), 10);
    const days = Number.isNaN(parsed) ? 30 : parsed;
    setActivityLoading(true);
    api.get(`/checkins/recent?days=${days}`)
      .then(r => setRecentActiveIds(r.data.user_ids || []))
      .catch(() => setRecentActiveIds([]))
      .finally(() => setActivityLoading(false));
  }, [activityFilter, canCheckActivity]);

  const viewClient = async (c) => {
    setSelectedClient(c);
    try {
      if (programFilter !== 'HER') {
        const r = await api.get(`/heat/completions?client_id=${c.id}`);
        setHeatCompletions(r.data);
      } else {
        setHeatCompletions([]);
      }
      if (programFilter !== 'HEAT') {
        const r2 = await api.get(`/her/completions?client_id=${c.id}`);
        setHerCompletions(r2.data);
      } else {
        setHerCompletions([]);
      }
    } catch {
      setHeatCompletions([]);
      setHerCompletions([]);
    }
    setDetailOpen(true);
  };

  const handleAssign = async () => {
    try {
      await api.post('/users/assign', assignForm);
      toast.success('Assignment successful!');
      setAssignOpen(false);
      load();
    } catch { toast.error('Assignment failed'); }
  };

  const programBadges = useMemo(() => {
    const programs = new Set();
    if (selectedClient?.profile?.programs) {
      selectedClient.profile.programs.forEach(p => programs.add(p));
    }
    if (heatCompletions.length) programs.add('HEAT');
    if (herCompletions.length) programs.add('HER');
    return Array.from(programs);
  }, [selectedClient, heatCompletions, herCompletions]);

  const filteredClients = useMemo(() => {
    let list = [...clients];
    if (statusFilter) {
      list = list.filter(c => (c.status || 'active').toLowerCase() === statusFilter);
    }
    if (assignedFilter === 'me') {
      list = list.filter(c => (user.assigned_clients || []).includes(c.id));
    }
    if (activityFilter) {
      if (activityLoading) return list;
      list = list.filter(c => recentActiveIds.includes(c.id));
    }
    return list;
  }, [clients, statusFilter, activityFilter, recentActiveIds, activityLoading, assignedFilter, user.assigned_clients]);

  const hasFilters = Boolean(statusFilter || activityFilter || programFilter || assignedFilter);

  const filterLabels = useMemo(() => {
    const labels = [];
    if (programFilter) labels.push(`Program: ${programFilter}`);
    if (statusFilter) labels.push(`Status: ${statusFilter}`);
    if (assignedFilter === 'me') labels.push('Assigned to me');
    if (activityFilter) {
      const days = activityFilter.replace('d', '') || '30';
      labels.push(`Active in last ${days} days`);
    }
    return labels;
  }, [programFilter, statusFilter, activityFilter, assignedFilter]);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Participants</h1>
          <p className="text-muted-foreground text-sm mt-1" data-testid="clients-count">
            {filteredClients.length} {hasFilters ? 'participants matching filters' : 'total participants'}
          </p>
        </div>
        {['coordinator', 'admin'].includes(user.role) && (
          <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="assign-client-button">
                <UserPlus size={16} /> Assign Participant
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Assign Participant to Specialist</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5"><Label>Participant</Label>
                  <Select value={assignForm.client_id} onValueChange={v => setAssignForm({ ...assignForm, client_id: v })}>
                    <SelectTrigger data-testid="assign-client-select"><SelectValue placeholder="Select participant" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Specialist</Label>
                  <Select value={assignForm.specialist_id} onValueChange={v => setAssignForm({ ...assignForm, specialist_id: v })}>
                    <SelectTrigger data-testid="assign-specialist-select"><SelectValue placeholder="Select specialist" /></SelectTrigger>
                    <SelectContent>{specialists.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAssign} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="confirm-assign-button">Assign</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {hasFilters && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-700 flex items-center justify-between" data-testid="clients-filter-summary">
          <span>Showing: {filterLabels.join(' • ') || 'Filtered results'}</span>
          {activityLoading && <span className="text-slate-500">Loading activity...</span>}
        </div>
      )}

      {isReferral && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800" data-testid="referral-readonly-disclaimer">
          Once submitted, referral information cannot be edited.
        </div>
      )}

      <div className="space-y-3">
        {filteredClients.map(c => (
          <Card key={c.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center text-base font-semibold text-teal-700 shrink-0">{c.full_name?.charAt(0)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{c.full_name}</p>
                <p className="text-xs text-muted-foreground">Programs: {(c.profile?.programs || []).join(', ') || programFilter || 'N/A'}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-50 text-emerald-700" variant="secondary">{c.status || 'active'}</Badge>
                <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" onClick={() => viewClient(c)} data-testid={`view-client-${c.id}`}><Eye size={14} /> View</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredClients.length === 0 && (
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-8 text-center">
              <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No participants found.</p>
            </CardContent>
          </Card>
        )}
      </div>
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader><DialogTitle>{selectedClient?.full_name}</DialogTitle></DialogHeader>
          {selectedClient && (
            <div className="space-y-4 pt-2">
              <div className="flex flex-wrap gap-2">
                {programBadges.length > 0 ? programBadges.map(program => (
                  <Badge key={program} variant="secondary" className="bg-teal-50 text-teal-700">{program}</Badge>
                )) : (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-600">No program flags</Badge>
                )}
              </div>

              <Card className="border-0 shadow-sm rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold">Classes & Accomplishments</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">HEAT Modules</p>
                    <div className="space-y-2">
                      {heatCompletions.length ? heatCompletions.map(c => (
                        <div key={c.id} className="p-3 rounded-xl bg-orange-50">
                          <p className="text-sm font-medium text-orange-900">{c.module_name}</p>
                          <p className="text-xs text-orange-700">Completed {new Date(c.completed_at).toLocaleDateString()}</p>
                        </div>
                      )) : (
                        <p className="text-xs text-muted-foreground">No HEAT accomplishments recorded.</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">HER Modules</p>
                    <div className="space-y-2">
                      {herCompletions.length ? herCompletions.map(c => (
                        <div key={c.id} className="p-3 rounded-xl bg-rose-50">
                          <p className="text-sm font-medium text-rose-900">{c.module_name}</p>
                          <p className="text-xs text-rose-700">Completed {new Date(c.completed_at).toLocaleDateString()}</p>
                        </div>
                      )) : (
                        <p className="text-xs text-muted-foreground">No HER accomplishments recorded.</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
